/**
 *
 * Inbound param application
 *
 * A peer's fade arrives as `parameters` at the sender's frame rate. Effect params go to the CHANNEL; `objects` and the settle stay on Redux.
 *
 * See context/param-channel.md.
 *
 */
import paramChannel from '~/lib/paramChannel'
import { EFFECT_SCHEMAS } from '~/lib/presetSettings'
import store from '~/store'
import { selectLive } from '~/store/live/selectors'
import { syncWireBaseline } from '~/store/middleware/liveSync'

// How long the stream must be quiet before the accumulated params settle into Redux. Longer than the sender's frame gap, so a running fade settles once at its end rather than on every lull.
const SETTLE_MS = 150

let settleTimer = null
// Everything the channel has taken since the last settle, so the Redux write names every key the fade touched.
let pending = null

const isEffect = key => key in EFFECT_SCHEMAS

// ⚠️ `meta.sync` is what stops the listener sending this straight back — the values came FROM the server.
const settle = () => {
	settleTimer = null
	if (!pending) return
	const payload = pending
	pending = null
	store.dispatch({ type: 'live/patchLiveObject', payload, meta: { sync: true, skipHistory: true } })
}

/**
 * Apply one inbound `parameters` payload.
 *
 * ⚠️ The channel takes effect params ONLY — `objects` is the scene graph, which it does not hold.
 */
export const applyInboundParams = payload => {
	const objects = payload.objects
	let touched = false
	// What this payload actually named, so the baseline advance below does not mark an untouched local edit as already-sent.
	const named = []

	for (const effect in payload) {
		if (!isEffect(effect)) continue
		const section = payload[effect]
		// ⚠️ `null` is the wire's effect-REMOVAL sentinel, and must be tested BEFORE the object guard that would otherwise skip it — a peer whose preset load pruned the effect keeps it at stale values forever and reads permanently dirty. Straight to Redux like `objects`, for the same reason: the channel holds values, not existence, so a deferred settle cannot express a removal.
		if (section === null) {
			store.dispatch({
				type: 'live/patchLiveObject',
				payload: { [effect]: null },
				meta: { sync: true, skipHistory: true },
			})
			// Anything still pending or in the channel for this effect would resurrect it on the next settle.
			if (pending) delete pending[effect]
			for (const key of paramChannel.keys(effect)) paramChannel.delete(`${effect}.${key}`)
			named.push(effect)
			touched = true
			continue
		}
		if (typeof section !== 'object' || Array.isArray(section)) continue
		for (const key in section) paramChannel.set(`${effect}.${key}`, section[key])
		;(pending ??= {})[effect] = { ...(pending[effect] ?? {}), ...section }
		named.push(effect)
		touched = true
	}

	// ⚠️ Straight to Redux, never deferred: `objects` carries null deletion sentinels the reducer must see, and nothing renders it off the channel.
	if (objects !== undefined) {
		store.dispatch({
			type: 'live/patchLiveObject',
			payload: { objects },
			meta: { sync: true, skipHistory: true },
		})
	}

	if (!touched) return

	// ⚠️ Advanced on EVERY message, not on the settle: the server already holds these values, so a fade's frames would otherwise read as local edits and echo back.
	//
	// ⚠️ SCOPED to the effects this payload named. A whole-baseline replace marks a local edit made inside the 33ms throttle window as already-sent, and the trailing send then diffs to empty — the edit is lost for good.
	syncWireBaseline(selectLive(store.getState()), objects !== undefined ? [...named, 'objects'] : named)

	if (settleTimer) clearTimeout(settleTimer)
	settleTimer = setTimeout(settle, SETTLE_MS)
}

/** Drop pending state — a reconnect re-handshakes, so nothing accumulated before it is still true. */
export const resetInboundParams = () => {
	if (settleTimer) clearTimeout(settleTimer)
	settleTimer = null
	pending = null
}
